import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import ColorWheel from "@/components/ColorWheel";
import shadesData from "@/data/beta_color_shades.json";
import rulesData from "@/data/beta_color_rules.json";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Beaker, Droplet, FlaskConical, Timer, Star, HelpCircle, ChevronDown, ChevronUp,
  Sparkles, AlertTriangle, Type, Brain, Loader2, Wand2, GitCompare,
} from "lucide-react";

// ---- Constants & helpers -----------------------------------------------
const META = shadesData._meta;
const SHADES = shadesData.shades;
const CORRECTION_MAP = shadesData.correction_map;
const SHADES_BY_CODE = Object.fromEntries(SHADES.map((s) => [s.code, s]));
const NON_NATURAL_SHADES = SHADES.filter((s) => !s.is_natural);
const NATURAL_BRUNETTE = SHADES.filter((s) => s.group === "Natural Brunette");

const REFLEX_TO_FAMILY = META.reflex_to_family;
const REFLEX_TO_SECTOR = META.reflex_to_sector;
const REFLEX_LABELS = META.reflex_labels;
const OBJECTIVE_TARGET_FAMILY = META.objective_target_family;

// ---- Code parser --------------------------------------------------------
// Accepts: "9.34", "9.3", "9.31", "10.03", "10.1", "9N", "10N", "5N"
// Returns: { level, primary, secondary, isNatural, family, secondaryFamily, sector, code, raw }
// or null if unparseable.
function parseObservedCode(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toUpperCase().replace(/\s+/g, "");
  if (!s) return null;

  // Naturali: "9N", "10N", "5N"
  const nMatch = s.match(/^(\d{1,2})N$/);
  if (nMatch) {
    const level = parseInt(nMatch[1], 10);
    if (level < 3 || level > 10) return null;
    return {
      raw: s, code: `${level}N`, level,
      primary: "0", secondary: null,
      family: "naturale", secondaryFamily: null,
      sector: null, secondarySector: null,
      isNatural: true, isClear: false,
    };
  }

  // Standard: "X.Y" or "X.YZ"  (X = 1-2 digits, Y/Z = 1 digit each)
  const match = s.match(/^(\d{1,2})\.(\d)(\d)?$/);
  if (!match) return null;
  const level = parseInt(match[1], 10);
  const primary = match[2];
  const secondary = match[3] || null;
  if (level < 3 || level > 10) return null;

  const primaryFamily = REFLEX_TO_FAMILY[primary] || null;
  const secondaryFamily = secondary ? (REFLEX_TO_FAMILY[secondary] || null) : null;

  // When primary is "0" (naturale), the dominant family is the secondary one.
  let family = primaryFamily;
  let sector = REFLEX_TO_SECTOR[primary];
  if (primary === "0" && secondary) {
    family = secondaryFamily;
    sector = REFLEX_TO_SECTOR[secondary];
  }

  return {
    raw: s,
    code: secondary ? `${level}.${primary}${secondary}` : `${level}.${primary}`,
    level, primary, secondary,
    family, secondaryFamily,
    sector, secondarySector: secondary ? REFLEX_TO_SECTOR[secondary] : null,
    isNatural: family === "naturale",
    isClear: false,
  };
}

// ---- Observation builder ------------------------------------------------
// Bridges a clicked shade OR a parsed text code into a unified observation object.
function buildObservationFromShade(shade) {
  if (!shade) return null;
  if (shade.is_natural) {
    return {
      raw: shade.code, code: shade.code, level: shade.level,
      primary: "0", secondary: null,
      family: "naturale", secondaryFamily: null,
      sector: null, secondarySector: null,
      isNatural: true, isClear: false,
      _shade: shade,
    };
  }
  return {
    raw: shade.code, code: shade.code, level: shade.level,
    primary: shade.primary_reflex,
    secondary: shade.secondary_reflex,
    family: shade.observed_family,
    secondaryFamily: shade.secondary_reflex ? REFLEX_TO_FAMILY[shade.secondary_reflex] : null,
    sector: shade.observed_sector,
    secondarySector: shade.secondary_reflex ? REFLEX_TO_SECTOR[shade.secondary_reflex] : null,
    isNatural: false, isClear: false,
    _shade: shade,
  };
}

// ---- Pigment pickers ----------------------------------------------------
// Pick the shade from the 55-database whose `observed_family` matches `family`,
// closest to targetLevel, excluding excludeCode. Falls back to nearest family if none.
function pickFromFamily(family, targetLevel, excludeCode = null) {
  if (!family) return null;
  let candidates = NON_NATURAL_SHADES.filter(
    (s) => s.observed_family === family && s.code !== excludeCode
  );
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => Math.abs(a.level - targetLevel) - Math.abs(b.level - targetLevel));
  return candidates[0];
}

function pickNaturalBrunette(targetLevel, excludeCode = null) {
  const candidates = NATURAL_BRUNETTE.filter((s) => s.code !== excludeCode);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => Math.abs(a.level - targetLevel) - Math.abs(b.level - targetLevel));
  return candidates[0];
}

function pickFromCodeList(codes, targetLevel, excludeCode = null) {
  const candidates = codes
    .map((c) => SHADES_BY_CODE[c])
    .filter((s) => s && s.code !== excludeCode);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => Math.abs(a.level - targetLevel) - Math.abs(b.level - targetLevel));
  return candidates[0];
}

// ---- Risk computation ---------------------------------------------------
function computeRisk(pigmentPct, porosity, mode) {
  if (mode === "none") return "nullo";
  if (porosity === "alta" && pigmentPct >= 20) return "alto";
  if (pigmentPct >= 22) return "medio";
  if (porosity === "alta" && pigmentPct >= 12) return "medio";
  return "basso";
}

// ---- Formula builder (Gloss philosophy) ---------------------------------
// Produces a formula based on observation + objective + intensity + porosity + totalGrams.
// Returns null if observation is missing.
function buildGlossFormula(observation, intensity, objective, porosity, totalGrams) {
  if (!observation) return null;

  const intensityProfile = rulesData.intensity_profile[intensity];
  const objMod = rulesData.objective_modifier[objective];
  const porMod = rulesData.porosity_modifier[porosity];
  const splitRule = rulesData.pigment_split_two_undertones;

  // Compute pigment percentage (Gloss = low pigment, lots of Clear)
  let pigmentPct = intensityProfile.pigment_pct * objMod.pigment_multiplier * porMod.pigment_multiplier;
  pigmentPct = Math.max(4, Math.min(35, pigmentPct));

  // Compute time
  const baseTime = intensityProfile.time_min;
  const timeMin = Math.round(baseTime * porMod.time_multiplier);
  const timeMax = Math.min(20, Math.round(timeMin + 2));
  const timeRange = `${timeMin}-${timeMax} min`;

  // ===== NATURAL OBSERVATION (no indesiderato) =====
  if (observation.isNatural) {
    if (objective === "naturale") {
      // No correction & no enrichment requested → just Clear gloss for shine
      const oxidantG = Math.round(totalGrams * 10) / 10;
      return {
        mode: "shine-only",
        pigments: [],
        clearGrams: totalGrams,
        oxidantGrams: oxidantG,
        oxidantVol: intensityProfile.oxidant_vol,
        oxidantPct: intensityProfile.oxidant_pct,
        timeRange, checkInterval: porMod.check_interval_min,
        pigmentPct: 0, otherPct: 100,
        resultText: "Base naturale senza riflesso da modificare. Gloss trasparente per amplificare lucentezza e setosità della fibra.",
        explanation: `${observation.code} è una nuance naturale pura, senza riflesso da correggere. ` +
          `Per aggiungere riflesso scegli un obiettivo diverso da "Naturale" (Dorato, Rame, Perla, Beige, Sabbia, Freddo). ` +
          `${porMod.note}`,
        risk: "nullo",
        tips: [
          "Gloss di luminosità: nessuna correzione attiva.",
          "Per arricchire scegli un obiettivo cromatico specifico.",
          `Controllo visivo ogni ${porMod.check_interval_min} minuti.`,
        ],
      };
    }
    // Enrich a natural base with desired reflex
    const target = OBJECTIVE_TARGET_FAMILY[objective];
    let primaryPigment;
    if (target === "naturale-perla") {
      primaryPigment = pickNaturalBrunette(observation.level);
    } else {
      primaryPigment = pickFromFamily(target, observation.level);
    }
    if (!primaryPigment) {
      return _emptyFormula(observation, intensityProfile, porMod, timeRange,
        `Nessun pigmento disponibile per l'obiettivo "${objMod.label}" al livello ${observation.level}.`);
    }
    const pigmentG = Math.round((totalGrams * pigmentPct / 100) * 10) / 10;
    const clearG = Math.round((totalGrams - pigmentG) * 10) / 10;
    const oxidantG = Math.round(totalGrams * 10) / 10;

    return {
      mode: "enrich",
      pigments: [{
        code: primaryPigment.code, grams: pigmentG, role: "arricchimento",
        desc: primaryPigment.name || `Riflesso ${objMod.label.toLowerCase()}`,
      }],
      clearGrams: clearG,
      oxidantGrams: oxidantG,
      oxidantVol: intensityProfile.oxidant_vol,
      oxidantPct: intensityProfile.oxidant_pct,
      timeRange, checkInterval: porMod.check_interval_min,
      pigmentPct: Math.round(pigmentPct),
      otherPct: 100 - Math.round(pigmentPct),
      resultText: objMod.result_text,
      explanation: `${observation.code} è una base naturale. ` +
        `Per ottenere un finale ${objMod.label.toLowerCase()} viene arricchita con ${primaryPigment.code} (${primaryPigment.name}). ` +
        `In filosofia Gloss il Clear domina la miscela e mantiene trasparenza. ${objMod.tip} ${porMod.note}`,
      risk: computeRisk(pigmentPct, porosity, "enrich"),
      tips: [
        "Applica su capelli tamponati, non bagnati.",
        "Emulsiona accuratamente i primi 30-40 secondi.",
        `Controllo visivo ogni ${porMod.check_interval_min} minuti.`,
        "Aumenta il Clear per ammorbidire il risultato, riducilo per intensificare.",
      ],
    };
  }

  // ===== COLOR OBSERVATION WITH REFLEX =====
  const isEnrich = objMod.mode === "enrich" || objMod.mode === "enrich-soft";
  const isNeutralize = objMod.mode === "neutralize";

  let primaryPigment = null;
  let secondaryPigment = null;

  if (isNeutralize) {
    // Objective = Naturale → neutralize observed indesiderato with COMPLEMENTARY
    const corr = CORRECTION_MAP[observation.family] || { primary: [], secondary: [] };
    primaryPigment = pickFromCodeList(corr.primary, observation.level, observation.code)
      || pickFromFamily(_familyComplementary(observation.family), observation.level, observation.code);
    if (observation.secondary && observation.secondaryFamily && observation.secondaryFamily !== observation.family) {
      const corrSec = CORRECTION_MAP[observation.secondaryFamily] || { primary: [], secondary: [] };
      secondaryPigment = pickFromCodeList(corrSec.primary, observation.level, observation.code)
        || pickFromFamily(_familyComplementary(observation.secondaryFamily), observation.level, observation.code);
      // Avoid duplicating the same pigment as primary
      if (secondaryPigment && primaryPigment && secondaryPigment.code === primaryPigment.code) {
        secondaryPigment = null;
      }
    }
  } else if (isEnrich) {
    const target = OBJECTIVE_TARGET_FAMILY[objective];
    if (target === "naturale-perla") {
      primaryPigment = pickNaturalBrunette(observation.level, observation.code);
    } else {
      primaryPigment = pickFromFamily(target, observation.level, observation.code);
    }
    // If observation has indesiderato CONFLICTING with target → add small complementary neutralizer
    const observedIsWarm = ["giallo", "giallo-arancio", "arancio", "rosso-arancio", "rosso"].includes(observation.family);
    const targetIsCool = ["blu", "viola-blu", "viola", "naturale-perla", "blu-verde"].includes(target);
    if (observedIsWarm && targetIsCool) {
      const corr = CORRECTION_MAP[observation.family] || { primary: [] };
      secondaryPigment = pickFromCodeList(corr.primary, observation.level, observation.code);
      if (secondaryPigment && primaryPigment && secondaryPigment.code === primaryPigment.code) {
        secondaryPigment = null;
      }
    }
  }

  // Compute grams
  const pigmentG = Math.round((totalGrams * pigmentPct / 100) * 10) / 10;
  const clearG = Math.round((totalGrams - pigmentG) * 10) / 10;
  const oxidantG = Math.round(totalGrams * 10) / 10;

  let gPrim = pigmentG;
  let gSec = 0;
  if (secondaryPigment) {
    gPrim = Math.round((pigmentG * splitRule.primary_pct / 100) * 10) / 10;
    gSec = Math.round((pigmentG - gPrim) * 10) / 10;
  }

  const pigments = [];
  if (primaryPigment) {
    pigments.push({
      code: primaryPigment.code, grams: gPrim,
      role: isNeutralize ? "neutralizzante primario" : "arricchimento primario",
      desc: primaryPigment.name || "",
    });
  }
  if (secondaryPigment) {
    pigments.push({
      code: secondaryPigment.code, grams: gSec,
      role: isNeutralize ? "neutralizzante secondario" : "correzione secondaria",
      desc: secondaryPigment.name || "",
    });
  }

  // Explanation strings
  const obsLabel = REFLEX_LABELS[observation.primary] || observation.family;
  const obsSecLabel = observation.secondary ? REFLEX_LABELS[observation.secondary] : null;
  let explanation;
  if (isNeutralize) {
    const corr = CORRECTION_MAP[observation.family] || {};
    explanation =
      `Sul capello è osservato ${observation.code} (${obsLabel}${obsSecLabel ? ` + ${obsSecLabel}` : ""}). ` +
      `${corr.note || ""} ` +
      `Pigmento ${primaryPigment ? primaryPigment.code : "—"}${secondaryPigment ? ` + ${secondaryPigment.code}` : ""} preso dalla colonna complementare. ` +
      `In filosofia Gloss il Clear domina la miscela: il riflesso resta morbido, mai coprente. ${porMod.note}`;
  } else {
    explanation =
      `Sul capello è osservato ${observation.code} (${obsLabel}${obsSecLabel ? ` + ${obsSecLabel}` : ""}). ` +
      `Obiettivo ${objMod.label}: si arricchisce il riflesso con ${primaryPigment ? primaryPigment.code : "—"}${secondaryPigment ? ` + piccola quota di ${secondaryPigment.code} per controllare l'indesiderato` : ""}. ` +
      `${objMod.tip} ${porMod.note}`;
  }

  return {
    mode: isNeutralize ? "neutralize" : "enrich",
    pigments,
    clearGrams: clearG,
    oxidantGrams: oxidantG,
    oxidantVol: intensityProfile.oxidant_vol,
    oxidantPct: intensityProfile.oxidant_pct,
    timeRange, checkInterval: porMod.check_interval_min,
    pigmentPct: Math.round(pigmentPct),
    otherPct: 100 - Math.round(pigmentPct),
    resultText: objMod.result_text,
    explanation,
    risk: computeRisk(pigmentPct, porosity, isNeutralize ? "neutralize" : "enrich"),
    tips: [
      "Applica su capelli tamponati, non bagnati.",
      "Emulsiona accuratamente i primi 30-40 secondi.",
      `Controllo visivo ogni ${porMod.check_interval_min} minuti.`,
      porosity === "alta"
        ? "Capello poroso: aumenta leggermente il Clear se serve maggiore controllo."
        : "Aumenta il Clear per ammorbidire il risultato, riducilo per intensificare il riflesso.",
    ],
  };
}

function _familyComplementary(family) {
  // Returns the textual complementary family name based on sector mapping
  const sectorByFamily = META.sectors.reduce((acc, s) => { acc[s.key] = s.idx; return acc; }, {});
  const idx = sectorByFamily[family];
  if (idx == null) return null;
  const compIdx = META.complementary_sector[String(idx)];
  return META.sectors[compIdx]?.key || null;
}

function _emptyFormula(observation, intensityProfile, porMod, timeRange, msg) {
  return {
    mode: "none",
    pigments: [],
    clearGrams: 0, oxidantGrams: 0,
    oxidantVol: intensityProfile.oxidant_vol,
    oxidantPct: intensityProfile.oxidant_pct,
    timeRange, checkInterval: porMod.check_interval_min,
    pigmentPct: 0, otherPct: 100,
    resultText: msg,
    explanation: msg,
    risk: "nullo",
    tips: [],
  };
}

// ---- Page ---------------------------------------------------------------
export default function BetaColor() {
  const [selectedShade, setSelectedShade] = useState(null); // from wheel
  const [typedCode, setTypedCode] = useState("");           // free text
  const [parsedTyped, setParsedTyped] = useState(null);     // parsed result of typedCode
  const [typedError, setTypedError] = useState("");
  const [source, setSource] = useState("wheel");            // "wheel" | "text"
  const [search, setSearch] = useState("");
  const [intensity, setIntensity] = useState("media");
  const [objective, setObjective] = useState("naturale");
  const [porosity, setPorosity] = useState("media");
  const [totalGrams, setTotalGrams] = useState(60);
  const [detailOpen, setDetailOpen] = useState(true);

  // AI Expert Mode
  const [mode, setMode] = useState("rules"); // "rules" | "ai"
  const [aiFormula, setAiFormula] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const sectorsMeta = META.sectors;

  // Parse typed code with debounce
  useEffect(() => {
    if (!typedCode.trim()) {
      setParsedTyped(null);
      setTypedError("");
      return;
    }
    const parsed = parseObservedCode(typedCode);
    if (parsed) {
      setParsedTyped(parsed);
      setTypedError("");
    } else {
      setParsedTyped(null);
      setTypedError("Codice non valido. Formato atteso: 9.34, 9.3, 8.13, 10.1, 7N…");
    }
  }, [typedCode]);

  // Active observation: text input wins when source === "text", else wheel shade
  const observation = useMemo(() => {
    if (source === "text" && parsedTyped) return parsedTyped;
    if (source === "wheel" && selectedShade) return buildObservationFromShade(selectedShade);
    return null;
  }, [source, parsedTyped, selectedShade]);

  // Wheel highlight: based on observation sector
  const highlightSectors = useMemo(() => {
    if (!observation || observation.sector == null) return { selected: [], complementary: [] };
    const obsIdx = observation.sector;
    const compIdx = META.complementary_sector[String(obsIdx)];
    return { selected: [obsIdx], complementary: compIdx != null ? [compIdx] : [] };
  }, [observation]);

  // Search results
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return SHADES.filter(
      (s) => s.code.toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [search]);

  const formula = useMemo(
    () => buildGlossFormula(observation, intensity, objective, porosity, totalGrams),
    [observation, intensity, objective, porosity, totalGrams]
  );

  // Display labels
  const obsCode = observation?.code || "—";
  const obsLevel = observation?.level || null;
  const obsPrimaryLabel = observation?.primary != null ? (REFLEX_LABELS[observation.primary] || "—") : "—";
  const obsSecondaryLabel = observation?.secondary ? (REFLEX_LABELS[observation.secondary] || "") : "";
  const obsFamilyLabel = observation?.isNatural
    ? "Naturale (nessun riflesso)"
    : (observation && observation.sector != null ? sectorsMeta[observation.sector].label : "—");
  const complementaryFamilyLabel = observation && observation.sector != null
    ? sectorsMeta[META.complementary_sector[String(observation.sector)]]?.label
    : null;

  const swatchColor = observation && observation.sector != null
    ? sectorsMeta[observation.sector].outer
    : observation?.isNatural ? "#C9AE7A" : "#E5E5E0";

  // Switch source automatically when user clicks a shade or types a code
  const handleSelectShade = (shade) => {
    setSelectedShade(shade);
    setSource("wheel");
    setTypedCode("");
  };
  const handleTypedChange = (val) => {
    setTypedCode(val);
    if (val.trim()) {
      setSource("text");
      setSelectedShade(null);
    }
  };

  // Reset AI formula whenever any input that affects formula changes
  useEffect(() => {
    setAiFormula(null);
    setAiError("");
  }, [observation?.code, intensity, objective, porosity, totalGrams]);

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const handleAIGenerate = async () => {
    if (!observation) {
      toast.error("Inserisci o seleziona una nuance prima di generare con AI.");
      return;
    }
    setAiLoading(true);
    setAiError("");
    setAiFormula(null);
    try {
      const payload = {
        observed_color: observation.code,
        goal: capitalize(objective),
        intensity: capitalize(intensity),
        porosity: capitalize(porosity),
        total_grams: totalGrams,
      };
      const { data } = await api.post("/beta-color/ai-formula", payload);
      setAiFormula(data.formula);
      toast.success("Formula AI Expert generata.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && (detail.message || detail)) || "Errore durante la generazione AI.";
      setAiError(typeof msg === "string" ? msg : "Errore durante la generazione AI.");
      toast.error(typeof msg === "string" ? msg : "Errore durante la generazione AI.");
    } finally {
      setAiLoading(false);
    }
  };

  // Auto-genera la formula AI quando si entra in modalità Confronta
  useEffect(() => {
    if (mode === "compare" && observation && !aiFormula && !aiLoading && !aiError) {
      handleAIGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, observation?.code]);

  const objectivesList = useMemo(
    () => Object.entries(rulesData.objective_modifier).map(([k, v]) => ({ value: k, label: v.label })),
    []
  );

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 animate-fade-up">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A]">AI Beta Color™</h1>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#FAF6EE] border border-[#C5A059]/40 text-[10px] font-semibold text-[#8A6B2E] tracking-wider" data-testid="beta-color-badge">
              BETA
            </span>
          </div>
          <p className="text-sm text-[#737373] tracking-wide">Assistente Gloss / Tonalizzazione professionale — 55 nuances Medavita Color Non Color</p>
        </div>
        <button
          type="button"
          className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] bg-white text-sm text-[#1A1A1A] transition"
          data-testid="beta-color-help"
          onClick={() => toast.info("Clicca una nuance sulla ruota oppure digita il codice osservato sul capello (es. 9.34). Il sistema calcola la formula di gloss in base all'obiettivo selezionato.")}
        >
          <HelpCircle className="w-4 h-4" /> Guida all'uso
        </button>
      </div>

      {/* MODE TOGGLE — Regole standard vs AI Expert */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-1.5 mb-6 inline-flex items-center gap-1 shadow-sm" data-testid="beta-color-mode-toggle">
        <button
          type="button"
          onClick={() => setMode("rules")}
          className={`h-9 px-4 rounded-xl text-xs font-medium transition inline-flex items-center gap-1.5 ${
            mode === "rules"
              ? "bg-[#1A1A1A] text-white"
              : "text-[#737373] hover:text-[#1A1A1A]"
          }`}
          data-testid="beta-color-mode-rules"
        >
          <FlaskConical className="w-3.5 h-3.5" /> Regole standard
        </button>
        <button
          type="button"
          onClick={() => setMode("ai")}
          className={`h-9 px-4 rounded-xl text-xs font-medium transition inline-flex items-center gap-1.5 ${
            mode === "ai"
              ? "bg-[#1A1A1A] text-white"
              : "text-[#737373] hover:text-[#1A1A1A]"
          }`}
          data-testid="beta-color-mode-ai"
        >
          <Brain className="w-3.5 h-3.5" /> AI Expert Mode™
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${
            mode === "ai" ? "bg-[#C5A059]/30 text-[#FBF3DF]" : "bg-[#FAF6EE] text-[#8A6B2E]"
          }`}>NEW</span>
        </button>
        <button
          type="button"
          onClick={() => setMode("compare")}
          className={`h-9 px-4 rounded-xl text-xs font-medium transition inline-flex items-center gap-1.5 ${
            mode === "compare"
              ? "bg-[#1A1A1A] text-white"
              : "text-[#737373] hover:text-[#1A1A1A]"
          }`}
          data-testid="beta-color-mode-compare"
        >
          <GitCompare className="w-3.5 h-3.5" /> Confronta
        </button>
      </div>

      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 items-start">
        {/* LEFT — Wheel + legend */}
        <div className="space-y-6 animate-fade-up">
          <ColorWheel observation={observation} />

          {/* Legend chips (lista colori sottostante per riferimento rapido) */}
          <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs text-[#1A1A1A]">
              {sectorsMeta.map((s) => (
                <div key={s.idx} className="inline-flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.outer }} />
                  {s.label}
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-[#737373] leading-relaxed flex items-start gap-2 px-1">
            <Sparkles className="w-3.5 h-3.5 text-[#C5A059] shrink-0 mt-0.5" />
            La ruota è uno strumento didattico. Le 55 nuance Medavita e le formule operative sono nei pannelli a destra.
          </p>
        </div>

        {/* RIGHT — Controls + formula */}
        <div className="space-y-5 animate-fade-up">

          {/* INPUT TESTO — Colore osservato */}
          <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8782] font-semibold">Colore osservato sul capello</p>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#737373]">
                <Type className="w-3 h-3" /> Inserimento manuale
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder="Es. 9.34, 8.13, 7.04, 9.3, 7N…"
                value={typedCode}
                onChange={(e) => handleTypedChange(e.target.value)}
                className={`w-full h-11 px-3 rounded-xl border bg-white focus:ring-2 outline-none text-sm font-semibold tabular-nums tracking-wider ${
                  typedError
                    ? "border-[#D9534F] focus:border-[#D9534F] focus:ring-[#D9534F]/20"
                    : "border-[#E5E5E0] focus:border-[#C5A059] focus:ring-[#C5A059]/20"
                }`}
                data-testid="beta-color-typed-code"
              />
              {parsedTyped && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-[#3FB665] font-semibold">
                  Riconosciuto
                </span>
              )}
            </div>
            {typedError && (
              <p className="mt-2 text-[11px] text-[#D9534F]" data-testid="beta-color-typed-error">{typedError}</p>
            )}
            {parsedTyped && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]" data-testid="beta-color-typed-summary">
                <p><span className="text-[#737373]">Livello: </span><span className="text-[#1A1A1A] font-medium">{parsedTyped.level}</span></p>
                <p><span className="text-[#737373]">Riflesso primario: </span><span className="text-[#1A1A1A] font-medium">.{parsedTyped.primary} {REFLEX_LABELS[parsedTyped.primary]}</span></p>
                {parsedTyped.secondary && (
                  <p className="col-span-2"><span className="text-[#737373]">Riflesso secondario: </span><span className="text-[#1A1A1A] font-medium">.{parsedTyped.secondary} {REFLEX_LABELS[parsedTyped.secondary]}</span></p>
                )}
                <p className="col-span-2">
                  <span className="text-[#737373]">Famiglia osservata: </span>
                  <span className="text-[#1A1A1A] font-medium">{parsedTyped.isNatural ? "Naturale" : (sectorsMeta[parsedTyped.sector]?.label || "—")}</span>
                  {parsedTyped.sector != null && (
                    <span className="text-[#C5A059] font-medium"> → complementare: {sectorsMeta[META.complementary_sector[String(parsedTyped.sector)]]?.label}</span>
                  )}
                </p>
              </div>
            )}
            <p className="text-[10px] text-[#A3A3A3] mt-2 leading-relaxed">
              Il colore osservato può essere qualsiasi codice reale (anche fuori dalla gamma Medavita). La formula sarà costruita esclusivamente con le 55 nuance del database Medavita Color Non Color.
            </p>
          </div>

          {/* Controls grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* SELECTED SHADE / SEARCH */}
            <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8782] font-semibold mb-3">Nuance attiva</p>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Cerca nuance Medavita…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none text-xs"
                  data-testid="beta-color-search"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E5E0] rounded-xl shadow-lg overflow-hidden">
                    {searchResults.map((r) => (
                      <button
                        key={r.code}
                        type="button"
                        onClick={() => { handleSelectShade(r); setSearch(""); }}
                        className="w-full px-3 py-2 text-left hover:bg-[#FAF6EE] flex items-center justify-between text-xs"
                      >
                        <span className="font-semibold text-[#1A1A1A]">{r.code}</span>
                        <span className="text-[#737373] truncate ml-2">{r.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {observation ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-14 h-14 rounded-xl shrink-0" style={{ background: swatchColor, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} />
                    <div className="min-w-0">
                      <p className="font-serif-display text-2xl text-[#1A1A1A]" data-testid="beta-color-shade-code">{obsCode}</p>
                      <p className="text-[11px] text-[#737373] leading-snug truncate">
                        {source === "text" ? "Inserito manualmente" : (observation._shade?.name || "—")}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <p><span className="text-[#737373]">Livello: </span><span className="text-[#1A1A1A]">{obsLevel ?? "—"}</span></p>
                    <p><span className="text-[#737373]">Riflesso: </span><span className="text-[#1A1A1A]" data-testid="beta-color-reflex">{obsPrimaryLabel}{obsSecondaryLabel ? ` + ${obsSecondaryLabel}` : ""}</span></p>
                    <p><span className="text-[#737373]">Famiglia: </span><span className="text-[#1A1A1A]" data-testid="beta-color-family">{obsFamilyLabel}</span></p>
                    {complementaryFamilyLabel && (
                      <p className="text-[#737373]">
                        Complementare: <span className="text-[#C5A059] font-medium" data-testid="beta-color-complementary">{complementaryFamilyLabel}</span>
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-xs text-[#A3A3A3] py-4">Nessuna nuance attiva. Digita un codice o clicca la ruota.</p>
              )}
            </div>

            {/* INTENSITÀ */}
            <ControlCard title="Intensità del gloss">
              <SegmentedGroup
                options={Object.entries(rulesData.intensity_profile).map(([k, v]) => ({ value: k, label: v.label }))}
                value={intensity}
                onChange={setIntensity}
                testidPrefix="beta-color-intensity"
              />
            </ControlCard>

            {/* OBIETTIVO — 7 options, responsive */}
            <ControlCard title="Obiettivo finale" className="sm:col-span-2">
              <SegmentedGroup
                options={objectivesList}
                value={objective}
                onChange={setObjective}
                small
                wrap
                testidPrefix="beta-color-objective"
              />
            </ControlCard>

            {/* POROSITÀ */}
            <ControlCard title="Porosità del capello">
              <SegmentedGroup
                options={Object.entries(rulesData.porosity_modifier).map(([k, v]) => ({ value: k, label: v.label }))}
                value={porosity}
                onChange={setPorosity}
                testidPrefix="beta-color-porosity"
              />
            </ControlCard>

            {/* GRAMS SLIDER */}
            <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8782] font-semibold">Quantità totale</p>
                <p className="text-2xl font-serif-display text-[#1A1A1A]" data-testid="beta-color-grams-value">{totalGrams} g</p>
              </div>
              <input
                type="range"
                min={10} max={120} step={5}
                value={totalGrams}
                onChange={(e) => setTotalGrams(Number(e.target.value))}
                className="w-full accent-[#C5A059]"
                data-testid="beta-color-grams-slider"
              />
              <div className="flex justify-between text-[10px] text-[#A3A3A3] mt-1 tracking-wider">
                <span>10 g</span><span>120 g</span>
              </div>
            </div>
          </div>

          {/* FORMULA CARD */}
          {!observation ? (
            <div className="rounded-2xl border border-dashed border-[#E5E5E0] bg-white p-10 text-center" data-testid="beta-color-empty">
              <FlaskConical className="w-10 h-10 mx-auto text-[#C5A059] mb-3" />
              <p className="font-serif-display text-2xl text-[#1A1A1A] mb-2">Inserisci o seleziona una nuance</p>
              <p className="text-sm text-[#737373] max-w-sm mx-auto">Digita il codice osservato sul capello (es. 9.34) oppure clicca un punto sulla ruota per generare la formula gloss.</p>
            </div>
          ) : mode === "compare" ? (
            <CompareView
              observation={observation}
              objective={objective}
              intensity={intensity}
              porosity={porosity}
              totalGrams={totalGrams}
              rulesFormula={formula}
              aiFormula={aiFormula}
              aiLoading={aiLoading}
              aiError={aiError}
              onGenerate={handleAIGenerate}
              objectivesList={objectivesList}
            />
          ) : mode === "ai" ? (
            <AIFormulaCard
              observation={observation}
              objective={objective}
              intensity={intensity}
              porosity={porosity}
              totalGrams={totalGrams}
              aiFormula={aiFormula}
              aiLoading={aiLoading}
              aiError={aiError}
              onGenerate={handleAIGenerate}
              detailOpen={detailOpen}
              setDetailOpen={setDetailOpen}
              objectivesList={objectivesList}
            />
          ) : (
            <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden" data-testid="beta-color-formula-card">
              <div className="px-5 py-3 border-b border-[#F2EEE7] flex items-center justify-between bg-[#FDFBF4]">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold">
                  Formula Gloss consigliata
                </p>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#737373] hover:text-[#1A1A1A]"
                  onClick={() => setDetailOpen((v) => !v)}
                  data-testid="beta-color-detail-toggle"
                >
                  Dettaglio tecnico {detailOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>

              <div className="grid sm:grid-cols-[1fr_180px] gap-0 sm:gap-4">
                <div className="p-5 space-y-2">
                  {formula?.pigments?.length === 0 && (
                    <p className="text-xs text-[#737373] italic">Nessun pigmento correttore necessario per questa combinazione.</p>
                  )}
                  {formula?.pigments?.map((p) => (
                    <FormulaRow
                      key={p.code}
                      icon={Beaker}
                      label={`${p.code} — ${p.role}`}
                      sub={p.desc ? `(${p.desc})` : ""}
                      value={`${p.grams} g`}
                      testid={`beta-color-pigment-${p.code}`}
                    />
                  ))}
                  {formula?.clearGrams > 0 && (
                    <FormulaRow icon={Droplet} label="Clear — Diluente trasparente" value={`${formula.clearGrams} g`} testid="beta-color-clear-row" />
                  )}
                  {formula && (
                    <FormulaRow
                      icon={FlaskConical}
                      label={`Ossidante ${formula.oxidantVol} vol (${formula.oxidantPct}%)`}
                      value={`${formula.oxidantGrams} g`}
                      testid="beta-color-oxidant"
                    />
                  )}
                  {formula && (
                    <FormulaRow
                      icon={Timer}
                      label="Tempo di posa indicativo"
                      sub={`Controllo visivo ogni ${formula.checkInterval} minuti`}
                      value={formula.timeRange}
                      testid="beta-color-time"
                    />
                  )}
                </div>

                {detailOpen && (
                  <div className="bg-[#FBF8F0] p-5 border-t sm:border-t-0 sm:border-l border-[#F2EEE7]" data-testid="beta-color-detail-panel">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold mb-3">% Composizione</p>
                    <Donut pigmentPct={formula?.pigmentPct || 0} />
                    <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mt-4 mb-1">Risultato atteso</p>
                    <p className="text-xs text-[#1A1A1A] leading-relaxed" data-testid="beta-color-result-atteso">{formula?.resultText}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E5E5E0]">
                      <span className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">Rischio spegnimento</span>
                      <RiskBadge level={formula?.risk} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WARNING TEXT — fixed professional (solo in modalità Regole) */}
          {observation && formula && mode === "rules" && (
            <div className="rounded-2xl border border-[#E8C97C]/60 bg-[#FFF8E6] p-4 flex items-start gap-3" data-testid="beta-color-warning">
              <AlertTriangle className="w-4 h-4 text-[#8A6B2E] shrink-0 mt-0.5" />
              <p className="text-[11.5px] text-[#5B4A24] leading-relaxed">
                {rulesData.warning_text}
              </p>
            </div>
          )}

          {/* SPIEGAZIONE + CONSIGLI (solo in modalità Regole) */}
          {observation && formula && mode === "rules" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8782] font-semibold mb-2">Spiegazione tecnica</p>
                <p className="text-xs text-[#1A1A1A] leading-relaxed" data-testid="beta-color-explanation">{formula.explanation}</p>
              </div>
              <div className="rounded-2xl border border-[#E5E5E0] bg-[#FBF8F0] p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold mb-2">Consigli professionali</p>
                <ul className="text-xs text-[#1A1A1A] leading-relaxed space-y-1.5">
                  {formula.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C5A059] mt-1.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* SAVE BUTTON */}
          {observation && (
            <button
              type="button"
              onClick={() => toast.success(`Formula per ${observation.code} salvata nei preferiti (BETA)`)}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] bg-white text-sm text-[#1A1A1A] transition"
              data-testid="beta-color-save"
            >
              <Star className="w-4 h-4 text-[#C5A059]" /> Salva formula nei preferiti
            </button>
          )}
        </div>
      </div>

      {/* Fullscreen wheel dialog rimosso in v6 (ruota didattica) */}
    </Layout>
  );
}

// ---- Helper components --------------------------------------------------

function ControlCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-[#E5E5E0] bg-white p-5 ${className}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8782] font-semibold mb-3">{title}</p>
      {children}
    </div>
  );
}

function SegmentedGroup({ options, value, onChange, small, wrap, testidPrefix }) {
  const cols = wrap
    ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-7"
    : options.length === 4
      ? "grid-cols-4"
      : "grid-cols-3";
  return (
    <div className={`grid gap-2 ${cols}`}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`h-10 rounded-xl border ${small ? "text-[11px]" : "text-xs"} font-medium transition px-1 ${
            value === o.value
              ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
              : "bg-white border-[#E5E5E0] text-[#1A1A1A] hover:border-[#C5A059]"
          }`}
          data-testid={`${testidPrefix}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FormulaRow({ icon: Icon, label, sub, value, testid }) {
  return (
    <div className="flex items-center gap-3 py-2" data-testid={testid}>
      <div className="w-8 h-8 rounded-lg bg-[#FBF8F0] border border-[#F2EEE7] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#C5A059]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#1A1A1A] truncate">{label}</p>
        {sub && <p className="text-[10.5px] text-[#737373] truncate">{sub}</p>}
      </div>
      <p className="text-sm font-semibold text-[#1A1A1A] tabular-nums">{value}</p>
    </div>
  );
}

function Donut({ pigmentPct }) {
  const size = 80;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pigmentPct / 100) * c;
  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EFE6D2" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="#8A6B2E"
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="text-xs text-[#1A1A1A] space-y-0.5">
        <p><span className="font-semibold">{pigmentPct}%</span> Pigmento</p>
        <p><span className="font-semibold">{100 - pigmentPct}%</span> Clear</p>
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  const cfg = {
    nullo:  { dot: "#A3A3A3", text: "Nullo" },
    basso:  { dot: "#3FB665", text: "Basso" },
    medio:  { dot: "#E8A12C", text: "Medio" },
    alto:   { dot: "#D9534F", text: "Alto" },
  }[level || "basso"];
  return (
    <span className="inline-flex items-center gap-2 text-xs text-[#1A1A1A]" data-testid="beta-color-risk">
      {cfg.text}
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.dot }} />
    </span>
  );
}

// ---- AI Expert Mode formula card ----------------------------------------
function AIFormulaCard({
  observation, objective, intensity, porosity, totalGrams,
  aiFormula, aiLoading, aiError, onGenerate, objectivesList,
}) {
  const objLabel = objectivesList.find((o) => o.value === objective)?.label || objective;

  // Build pigment rows from AI output
  const pigmentRows = aiFormula
    ? [
        aiFormula.primary_pigment && {
          code: aiFormula.primary_pigment,
          grams: aiFormula.primary_grams,
          role: "pigmento primario",
        },
        aiFormula.secondary_pigment && {
          code: aiFormula.secondary_pigment,
          grams: aiFormula.secondary_grams,
          role: "pigmento secondario",
        },
      ].filter(Boolean)
    : [];

  const totalPigmentG = pigmentRows.reduce((s, p) => s + (p.grams || 0), 0);
  const pigmentPct = aiFormula
    ? Math.round((totalPigmentG / totalGrams) * 100)
    : 0;

  return (
    <div className="rounded-2xl border border-[#1A1A1A] bg-white overflow-hidden shadow-sm" data-testid="beta-color-ai-card">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[#F2EEE7] flex items-center justify-between bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A]">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#E8C97C] font-semibold inline-flex items-center gap-2">
          <Brain className="w-3.5 h-3.5" /> AI Expert Mode™ — formula AI
        </p>
        {aiFormula && (
          <span className="text-[10px] uppercase tracking-wider text-white/60">
            Modello: Claude Sonnet 4.5
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Input summary */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5 text-[11px]">
          <Param label="Osservato" value={observation.code} />
          <Param label="Obiettivo" value={objLabel} />
          <Param label="Intensità" value={intensity} />
          <Param label="Porosità" value={porosity} />
          <Param label="Totale" value={`${totalGrams} g`} />
        </div>

        {/* No formula yet → CTA */}
        {!aiFormula && !aiLoading && !aiError && (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FAF6EE] border border-[#C5A059]/40 mb-4">
              <Wand2 className="w-6 h-6 text-[#C5A059]" />
            </div>
            <p className="font-serif-display text-xl text-[#1A1A1A] mb-2">Ragionamento AI esperto</p>
            <p className="text-xs text-[#737373] max-w-md mx-auto mb-5 leading-relaxed">
              L'AI analizzerà il colore osservato, l'obiettivo e la porosità per generare una formula
              gloss professionale realistica, utilizzando esclusivamente le 55 nuance Medavita Choice Color Non Color.
            </p>
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#2A2A2A] transition shadow-md"
              data-testid="beta-color-ai-generate"
            >
              <Brain className="w-4 h-4" /> Genera formula AI
            </button>
          </div>
        )}

        {/* Loading */}
        {aiLoading && (
          <div className="text-center py-10" data-testid="beta-color-ai-loading">
            <Loader2 className="w-8 h-8 text-[#C5A059] mx-auto mb-3 animate-spin" />
            <p className="text-sm text-[#1A1A1A] font-medium mb-1">L'AI sta ragionando…</p>
            <p className="text-xs text-[#737373]">Analisi del riflesso osservato, scelta dei pigmenti complementari e bilanciamento Clear.</p>
          </div>
        )}

        {/* Error */}
        {aiError && !aiLoading && (
          <div className="rounded-xl border border-[#D9534F]/40 bg-[#FFF4F2] p-4 mb-4" data-testid="beta-color-ai-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#D9534F] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-[#7B2A24] font-semibold mb-1">Errore generazione AI</p>
                <p className="text-[11px] text-[#7B2A24] leading-relaxed">{aiError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onGenerate}
              className="mt-3 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-[#1A1A1A] text-white text-[11px] font-medium"
              data-testid="beta-color-ai-retry"
            >
              Riprova
            </button>
          </div>
        )}

        {/* Formula result */}
        {aiFormula && !aiLoading && (
          <div className="space-y-4" data-testid="beta-color-ai-formula">
            {/* Pigment rows */}
            <div className="space-y-1">
              {pigmentRows.map((p) => (
                <FormulaRow
                  key={p.code}
                  icon={Beaker}
                  label={`${p.code} — ${p.role}`}
                  value={`${p.grams} g`}
                  testid={`beta-color-ai-pigment-${p.code}`}
                />
              ))}
              <FormulaRow
                icon={Droplet}
                label="Clear — Diluente trasparente"
                value={`${aiFormula.clear_grams} g`}
                testid="beta-color-ai-clear"
              />
              <FormulaRow
                icon={FlaskConical}
                label={`Ossidante ${aiFormula.developer}`}
                value={`${totalGrams} g`}
                testid="beta-color-ai-developer"
              />
              <FormulaRow
                icon={Timer}
                label="Tempo di posa"
                value={aiFormula.processing_time}
                testid="beta-color-ai-time"
              />
            </div>

            {/* Donut + result */}
            <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-[#F2EEE7]">
              <div className="bg-[#FBF8F0] rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold mb-3">% Composizione</p>
                <Donut pigmentPct={pigmentPct} />
              </div>
              <div className="bg-[#FBF8F0] rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold mb-2">Risultato atteso</p>
                <p className="text-xs text-[#1A1A1A] leading-relaxed">{aiFormula.expected_result}</p>
              </div>
            </div>

            {/* Rationale + notes */}
            <div className="rounded-xl border border-[#1A1A1A]/10 bg-[#1A1A1A]/[0.03] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold mb-2 inline-flex items-center gap-1.5">
                <Brain className="w-3 h-3 text-[#C5A059]" /> Ragionamento AI
              </p>
              <p className="text-xs text-[#1A1A1A] leading-relaxed" data-testid="beta-color-ai-rationale">{aiFormula.rationale}</p>
            </div>

            <div className="rounded-xl border border-[#E5E5E0] bg-white p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Note tecniche</p>
              <p className="text-xs text-[#1A1A1A] leading-relaxed">{aiFormula.technical_notes}</p>
            </div>

            {/* Warning */}
            <div className="rounded-xl border border-[#E8C97C]/60 bg-[#FFF8E6] p-3 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-[#8A6B2E] shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#5B4A24] leading-relaxed">
                {rulesData.warning_text}
              </p>
            </div>

            {/* Regenerate */}
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-[#E5E5E0] hover:border-[#C5A059] bg-white text-[11px] text-[#1A1A1A] transition"
              data-testid="beta-color-ai-regenerate"
            >
              <Wand2 className="w-3.5 h-3.5" /> Rigenera con AI
            </button>

            {aiFormula.warnings && aiFormula.warnings.length > 0 && (
              <p className="text-[10px] text-[#A3A3A3] italic">
                {aiFormula.warnings.join(" · ")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Param({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">{label}</p>
      <p className="text-xs text-[#1A1A1A] font-semibold mt-0.5">{value}</p>
    </div>
  );
}

// ---- Compare view: rules vs AI side by side -----------------------------
function CompareView({
  observation, objective, intensity, porosity, totalGrams,
  rulesFormula, aiFormula, aiLoading, aiError, onGenerate, objectivesList,
}) {
  const objLabel = objectivesList.find((o) => o.value === objective)?.label || objective;

  // Normalize both into a unified comparable shape
  const rulesNorm = useMemo(() => normalizeRules(rulesFormula, totalGrams), [rulesFormula, totalGrams]);
  const aiNorm = useMemo(() => normalizeAI(aiFormula, totalGrams), [aiFormula, totalGrams]);

  return (
    <div className="space-y-5" data-testid="beta-color-compare">
      {/* Header with input summary */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold inline-flex items-center gap-2">
            <GitCompare className="w-3.5 h-3.5" /> Confronto formule
          </p>
          {aiNorm && (
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#737373] hover:text-[#1A1A1A]"
              data-testid="beta-color-compare-regenerate"
            >
              <Wand2 className="w-3 h-3" /> Rigenera AI
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-[11px]">
          <Param label="Osservato" value={observation.code} />
          <Param label="Obiettivo" value={objLabel} />
          <Param label="Intensità" value={intensity} />
          <Param label="Porosità" value={porosity} />
          <Param label="Totale" value={`${totalGrams} g`} />
        </div>
      </div>

      {/* Two columns */}
      <div className="grid md:grid-cols-2 gap-4">
        <CompactFormulaCard
          variant="rules"
          title="Regole standard"
          subtitle="Sistema deterministico"
          icon={FlaskConical}
          data={rulesNorm}
        />
        <CompactFormulaCard
          variant="ai"
          title="AI Expert Mode™"
          subtitle="Claude Sonnet 4.5"
          icon={Brain}
          data={aiNorm}
          loading={aiLoading}
          error={aiError}
          onRetry={onGenerate}
        />
      </div>

      {/* Differences box */}
      {rulesNorm && aiNorm && (
        <DifferencesBox rules={rulesNorm} ai={aiNorm} totalGrams={totalGrams} />
      )}

      {/* Shared warning */}
      <div className="rounded-2xl border border-[#E8C97C]/60 bg-[#FFF8E6] p-4 flex items-start gap-3" data-testid="beta-color-compare-warning">
        <AlertTriangle className="w-4 h-4 text-[#8A6B2E] shrink-0 mt-0.5" />
        <p className="text-[11.5px] text-[#5B4A24] leading-relaxed">
          {rulesData.warning_text}
        </p>
      </div>
    </div>
  );
}

// Normalize rules formula into a comparable shape
function normalizeRules(formula, totalGrams) {
  if (!formula) return null;
  const pigments = (formula.pigments || []).map((p) => ({
    code: p.code, grams: p.grams, role: p.role, desc: p.desc || "",
  }));
  return {
    source: "rules",
    pigments,
    clearGrams: formula.clearGrams || 0,
    pigmentPct: formula.pigmentPct || 0,
    oxidantText: `${formula.oxidantGrams || totalGrams} g · ${formula.oxidantVol} vol`,
    timeText: formula.timeRange || "—",
    resultText: formula.resultText || "",
    rationale: formula.explanation || "",
    notes: (formula.tips || []).slice(0, 2).join(" "),
  };
}

function normalizeAI(aiFormula, totalGrams) {
  if (!aiFormula) return null;
  const pigments = [];
  if (aiFormula.primary_pigment) {
    pigments.push({
      code: aiFormula.primary_pigment, grams: aiFormula.primary_grams,
      role: "primario", desc: "",
    });
  }
  if (aiFormula.secondary_pigment) {
    pigments.push({
      code: aiFormula.secondary_pigment, grams: aiFormula.secondary_grams,
      role: "secondario", desc: "",
    });
  }
  const totalPig = pigments.reduce((s, p) => s + (p.grams || 0), 0);
  const pct = totalGrams > 0 ? Math.round((totalPig / totalGrams) * 100) : 0;
  return {
    source: "ai",
    pigments,
    clearGrams: aiFormula.clear_grams || 0,
    pigmentPct: pct,
    oxidantText: `${totalGrams} g · ${aiFormula.developer || "5 vol"}`,
    timeText: aiFormula.processing_time || "15-20 minuti (indicativo)",
    resultText: aiFormula.expected_result || "",
    rationale: aiFormula.rationale || "",
    notes: aiFormula.technical_notes || "",
  };
}

// Compact card used in compare view
function CompactFormulaCard({ variant, title, subtitle, icon: Icon, data, loading, error, onRetry }) {
  const isAi = variant === "ai";
  const headerClass = isAi
    ? "bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] text-white"
    : "bg-[#FDFBF4] text-[#8A6B2E]";
  const accent = isAi ? "#E8C97C" : "#8A6B2E";

  return (
    <div className={`rounded-2xl border ${isAi ? "border-[#1A1A1A]" : "border-[#E5E5E0]"} bg-white overflow-hidden shadow-sm flex flex-col`} data-testid={`beta-color-compare-${variant}`}>
      <div className={`px-4 py-3 ${headerClass} flex items-center justify-between`}>
        <p className="text-[11px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {title}
        </p>
        <span className={`text-[9px] uppercase tracking-wider ${isAi ? "text-white/60" : "text-[#A3A3A3]"}`}>
          {subtitle}
        </span>
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        {loading && (
          <div className="text-center py-10" data-testid={`beta-color-compare-${variant}-loading`}>
            <Loader2 className="w-7 h-7 text-[#C5A059] mx-auto mb-2 animate-spin" />
            <p className="text-xs text-[#1A1A1A] font-medium">L'AI sta ragionando…</p>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-[#D9534F]/40 bg-[#FFF4F2] p-3" data-testid={`beta-color-compare-${variant}-error`}>
            <p className="text-[11px] text-[#7B2A24] mb-2">{error}</p>
            <button
              type="button" onClick={onRetry}
              className="inline-flex items-center gap-1 h-7 px-3 rounded-md bg-[#1A1A1A] text-white text-[10px] font-medium"
            >
              Riprova
            </button>
          </div>
        )}

        {!loading && !error && !data && (
          <p className="text-xs text-[#A3A3A3] italic py-8 text-center">Nessuna formula disponibile.</p>
        )}

        {!loading && !error && data && (
          <>
            {/* Pigment rows */}
            <div className="space-y-1.5">
              {data.pigments.length === 0 ? (
                <p className="text-[11px] text-[#737373] italic">Nessun pigmento.</p>
              ) : (
                data.pigments.map((p) => (
                  <div key={p.code} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-[#FBF8F0]">
                    <div className="min-w-0 flex items-center gap-2">
                      <Beaker className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                      <span className="text-xs text-[#1A1A1A] font-semibold tabular-nums">{p.code}</span>
                      <span className="text-[10px] text-[#737373] truncate">{p.role}</span>
                    </div>
                    <span className="text-xs font-semibold text-[#1A1A1A] tabular-nums">{p.grams} g</span>
                  </div>
                ))
              )}
              <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-[#FBF8F0]">
                <div className="flex items-center gap-2">
                  <Droplet className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                  <span className="text-xs text-[#1A1A1A]">Clear</span>
                </div>
                <span className="text-xs font-semibold text-[#1A1A1A] tabular-nums">{data.clearGrams} g</span>
              </div>
              <div className="flex items-center justify-between gap-2 py-1.5 px-2">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                  <span className="text-xs text-[#1A1A1A]">Ossidante</span>
                </div>
                <span className="text-[11px] text-[#1A1A1A] tabular-nums">{data.oxidantText}</span>
              </div>
              <div className="flex items-center justify-between gap-2 py-1.5 px-2">
                <div className="flex items-center gap-2">
                  <Timer className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                  <span className="text-xs text-[#1A1A1A]">Tempo di posa</span>
                </div>
                <span className="text-[11px] text-[#1A1A1A]">{data.timeText}</span>
              </div>
            </div>

            {/* % composition */}
            <div className="flex items-center justify-between pt-2 border-t border-[#F2EEE7]">
              <span className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">% Pigmento</span>
              <span className="text-sm font-semibold text-[#1A1A1A]">{data.pigmentPct}%</span>
            </div>

            {/* Result */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1">Risultato atteso</p>
              <p className="text-[11.5px] text-[#1A1A1A] leading-relaxed">{data.resultText || "—"}</p>
            </div>

            {/* Notes */}
            {data.notes && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1">Note tecniche</p>
                <p className="text-[11.5px] text-[#1A1A1A] leading-relaxed">{data.notes}</p>
              </div>
            )}

            {/* Rationale */}
            <div className={`rounded-lg p-3 mt-auto ${isAi ? "bg-[#1A1A1A]/[0.04] border border-[#1A1A1A]/10" : "bg-[#FAF6EE] border border-[#C5A059]/20"}`}>
              <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: accent }}>
                {isAi ? "Ragionamento AI" : "Spiegazione tecnica"}
              </p>
              <p className="text-[11px] text-[#1A1A1A] leading-relaxed">{data.rationale || "—"}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Differences summary box
function DifferencesBox({ rules, ai, totalGrams }) {
  const rulesPigmentSet = new Set(rules.pigments.map((p) => p.code));
  const aiPigmentSet = new Set(ai.pigments.map((p) => p.code));

  const onlyRules = [...rulesPigmentSet].filter((c) => !aiPigmentSet.has(c));
  const onlyAi = [...aiPigmentSet].filter((c) => !rulesPigmentSet.has(c));
  const common = [...rulesPigmentSet].filter((c) => aiPigmentSet.has(c));

  const clearDiff = Math.round((ai.clearGrams - rules.clearGrams) * 10) / 10;
  const rulesPct = totalGrams > 0 ? Math.round((rules.clearGrams / totalGrams) * 100) : 0;
  const aiPct = totalGrams > 0 ? Math.round((ai.clearGrams / totalGrams) * 100) : 0;
  const sameTime = rules.timeText === ai.timeText;

  // Build a human commentary
  const commentary = buildCommentary({ onlyRules, onlyAi, common, clearDiff, sameTime, ai, rules });

  return (
    <div className="rounded-2xl border border-[#C5A059]/40 bg-[#FAF6EE] p-5" data-testid="beta-color-differences">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold mb-3 inline-flex items-center gap-2">
        <GitCompare className="w-3.5 h-3.5" /> Differenze tra le due formule
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <DiffStat
          label="Pigmenti comuni"
          value={common.length}
          detail={common.length > 0 ? common.join(", ") : "—"}
        />
        <DiffStat
          label="Clear (∆)"
          value={`${clearDiff > 0 ? "+" : ""}${clearDiff} g`}
          detail={`Regole ${rulesPct}% · AI ${aiPct}%`}
          highlight={Math.abs(clearDiff) > totalGrams * 0.1}
        />
        <DiffStat
          label="Tempo di posa"
          value={sameTime ? "Uguale" : "Diverso"}
          detail={`R: ${rules.timeText} · AI: ${ai.timeText}`}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg bg-white border border-[#E5E5E0] p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1">Solo nelle Regole</p>
          {onlyRules.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {onlyRules.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-md bg-[#FBF8F0] border border-[#E5E5E0] text-[11px] font-semibold text-[#1A1A1A]">{c}</span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-[#A3A3A3] italic">Nessuno</p>
          )}
        </div>
        <div className="rounded-lg bg-white border border-[#1A1A1A]/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1">Solo nell'AI</p>
          {onlyAi.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {onlyAi.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-md bg-[#1A1A1A] text-[11px] font-semibold text-white">{c}</span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-[#A3A3A3] italic">Nessuno</p>
          )}
        </div>
      </div>

      <div className="rounded-lg bg-white border border-[#E5E5E0] p-3">
        <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1.5">Commento sintetico</p>
        <p className="text-[12px] text-[#1A1A1A] leading-relaxed" data-testid="beta-color-differences-commentary">
          {commentary}
        </p>
      </div>
    </div>
  );
}

function DiffStat({ label, value, detail, highlight }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "bg-white border-[#C5A059]/60" : "bg-white border-[#E5E5E0]"}`}>
      <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">{label}</p>
      <p className={`font-serif-display text-xl mt-0.5 ${highlight ? "text-[#8A6B2E]" : "text-[#1A1A1A]"}`}>{value}</p>
      <p className="text-[10px] text-[#737373] mt-1 leading-snug">{detail}</p>
    </div>
  );
}

function buildCommentary({ onlyRules, onlyAi, common, clearDiff, sameTime, ai, rules }) {
  const phrases = [];

  if (common.length === rules.pigments.length && common.length === ai.pigments.length && common.length > 0) {
    phrases.push(`Le due formule scelgono gli stessi pigmenti (${common.join(", ")}).`);
  } else if (onlyAi.length > 0 && onlyRules.length > 0) {
    const aiPart = ai.pigments.filter((p) => onlyAi.includes(p.code))
      .map((p) => `${p.code} (${p.grams}g)`).join(" + ");
    const rulesPart = rules.pigments.filter((p) => onlyRules.includes(p.code))
      .map((p) => `${p.code} (${p.grams}g)`).join(" + ");
    phrases.push(`L'AI ha scelto ${aiPart || onlyAi.join("+")} mentre le regole hanno proposto ${rulesPart || onlyRules.join("+")}.`);
  } else if (onlyAi.length > 0) {
    phrases.push(`L'AI aggiunge ${onlyAi.join(", ")} che non è presente nella formula deterministica.`);
  } else if (onlyRules.length > 0) {
    phrases.push(`Le regole usano ${onlyRules.join(", ")} che l'AI non ha selezionato.`);
  }

  if (Math.abs(clearDiff) >= 3) {
    if (clearDiff > 0) {
      phrases.push(`L'AI alza la quota di Clear di ${clearDiff}g (più trasparenza, riflesso più morbido).`);
    } else {
      phrases.push(`L'AI riduce il Clear di ${Math.abs(clearDiff)}g (riflesso leggermente più intenso).`);
    }
  } else {
    phrases.push("Il bilanciamento Clear è coerente tra le due formule.");
  }

  if (!sameTime) {
    phrases.push("I tempi di posa proposti differiscono — verifica visivamente l'evoluzione.");
  }

  // Stylistic closing
  phrases.push("Il ragionamento AI è solitamente più creativo e si avvicina al pensiero di un colorista esperto, mentre le regole garantiscono una baseline conservativa.");

  return phrases.join(" ");
}


